<template>
  <div>
    <van-field
      v-model="displayValue"
      is-link
      readonly
      :label="label"
      :placeholder="placeholder"
      :disabled="disabled"
      @click="showPopup"
    />
    <van-popup v-model="show" round position="bottom">
      <van-cascader
        v-model="activeValue"
        title="请选择所在地区"
        :options="options"
        :loading="loading"
        show-toolbar
        @close="show = false"
        @finish="onFinish"
        @change="onChange"
        @cancel="onCancel"
      />
    </van-popup>
  </div>
</template>

<script>
import { getSelectorCity } from "@/service/api/selector";

export default {
  name: "AreaSelect",
  props: {
    value: {
      type: [String, Number],
      default: "",
    },
    label: {
      type: String,
      default: "地区",
    },
    placeholder: {
      type: String,
      default: "请选择所在地区",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show: false,
      loading: false,
      options: [],
      activeValue: null,
      selectedPath: [],
      selectedNames: [],
    };
  },
  computed: {
    displayValue() {
      if (this.selectedNames.length > 0) {
        return this.selectedNames.join("/");
      }
      return "";
    },
  },
  watch: {
    value(newVal) {
      if (newVal && newVal !== this.getCurrentSelectedId()) {
        this.initSelectedValue();
      }
    },
  },
  mounted() {
    this.loadAreaData();
  },
  created() {
    // 如果value已有值，等待options加载完成后再初始化
    if (this.value) {
      const unwatch = this.$watch("options", () => {
        if (this.options.length > 0) {
          this.initSelectedValue();
          unwatch();
        }
      });
    }
  },
  methods: {
    // 加载地区数据
    loadAreaData() {
      // 避免重复加载
      if (this.loading || this.options.length > 0) return;
      this.loading = true;
      getSelectorCity()
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.options = this.buildCascaderOptions(res.data);
            this.initSelectedValue();
          }
        })
        .catch((err) => {
          console.error("加载地区数据失败", err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 构建级联选择器需要的数据结构
    buildCascaderOptions(data) {
      const map = {};
      const root = [];

      // 创建映射表
      data.forEach((item) => {
        map[item.id] = {
          text: item.name,
          value: item.id,
        };
        // 只有有子节点时才添加children属性
        const hasChildren = data.some(c => c.parentId === item.id);
        if (hasChildren) {
          map[item.id].children = [];
        }
      });

      // 构建树形结构
      data.forEach((item) => {
        if (item.parentId) {
          if (map[item.parentId] && map[item.parentId].children) {
            map[item.parentId].children.push(map[item.id]);
          }
        } else {
          root.push(map[item.id]);
        }
      });

      return root;
    },
    // 根据value查找完整路径
    findPath(targetId, currentOptions, path) {
      for (const item of currentOptions) {
        const newPath = [...path, item.value];
        if (item.value === targetId) {
          return newPath;
        }
        if (item.children && item.children.length > 0) {
          const result = this.findPath(targetId, item.children, newPath);
          if (result) {
            return result;
          }
        }
      }
      return null;
    },
    // 初始化选中值
    initSelectedValue() {
      if (!this.value) {
        this.activeValue = null;
        this.selectedPath = [];
        this.selectedNames = [];
        return;
      }

      const path = this.findPath(this.value, this.options, []);
      if (path) {
        this.selectedPath = path;
        this.activeValue = this.value;
        this.updateSelectedNames();
      }
    },
    // 获取当前选中的ID
    getCurrentSelectedId() {
      if (this.selectedPath.length > 0) {
        return this.selectedPath[this.selectedPath.length - 1];
      }
      return "";
    },
    // 更新选中名称
    updateSelectedNames() {
      this.selectedNames = [];
      let currentOptions = this.options;

      this.selectedPath.forEach((value) => {
        const item = currentOptions.find((opt) => opt.value === value);
        if (item) {
          this.selectedNames.push(item.text);
          currentOptions = item.children || [];
        }
      });
    },
    // 从当前value构建路径
    buildPathFromValue(value, options) {
      const path = [];
      let currentOptions = options;
      let currentValue = value;

      while (currentValue) {
        const item = currentOptions.find(opt => opt.value === currentValue);
        if (item) {
          path.push(item.value);
          currentOptions = item.children || [];
          if (item.children && item.children.length > 0) {
            // 如果还有子节点，说明没选完，取最后一个
            currentValue = null;
          } else {
            currentValue = null;
          }
        } else {
          currentValue = null;
        }
      }

      return path;
    },
    // 显示弹窗
    showPopup() {
      if (!this.disabled) {
        this.show = true;
        // 打开弹窗时，同步当前选中值
        if (this.value) {
          this.activeValue = this.value;
          this.selectedPath = this.findPath(this.value, this.options, []) || [];
          this.updateSelectedNames();
        } else {
          this.activeValue = null;
          this.selectedPath = [];
          this.selectedNames = [];
        }
      }
    },
    // 级联选择变化
    onChange({ value }) {
      this.selectedPath = this.buildPathFromValue(value, this.options);
      this.updateSelectedNames();
    },
    // 选择完成
    onFinish({ value }) {
      this.selectedPath = this.buildPathFromValue(value, this.options);
      this.updateSelectedNames();
      const selectedId = value;
      this.$emit("input", selectedId);
      this.$emit("change", selectedId, this.selectedNames);
      this.show = false;
    },
    // 取消选择
    onCancel() {
      // 取消时恢复原来的值
      this.initSelectedValue();
      this.show = false;
    },
  },
};
</script>

<style lang="less" scoped></style>
